/* eslint-disable max-depth */
/**
 * Turns a (possibly nested) object/array into a query string using bracket notation,
 * e.g. { a: 1, b: { c: 2 }, d: [10, 20] } → "a=1&b[c]=2&d[0]=10&d[1]=20".
 * By default, keys and values are URL-encoded. Disable via options.encode = false.
 *
 * @param input the object/array that is going to be converted
 * @param options.prefix optional prefix to add in front of the entire output string
 * @param options.encode whether to URL-encode keys/values (default: true)
 * @returns the input object in a query-string format
 */
export function serializeObject(
  input: unknown,
  options?: { prefix?: string; encode?: boolean },
): string {
  const parts: string[] = [];
  const seen = new WeakSet<object>();
  const shouldEncode = options?.encode !== false;

  const enc = (x: unknown) => (shouldEncode ? encodeURIComponent(String(x)) : String(x));

  const push = (name: string, value: unknown) => {
    if (value === undefined || value === null) return;
    const val = value instanceof Date ? value.toISOString() : value;
    parts.push(`${name}=${enc(val)}`);
  };

  const buildName = (path: (string | number)[]) => {
    const [first, ...rest] = path;
    // Encode segments, not the brackets themselves.
    return [enc(first), ...rest.map((k) => `[${enc(k)}]`)].join('');
  };

  const walk = (value: unknown, path: (string | number)[]) => {
    // remove null or undefined values or empty strings (also trims whitespace-only values)
    if (value === null || value === undefined || (typeof value === 'string' && value.trim() === ''))
      return;

    if (typeof value === 'object') {
      const obj = value as Record<string, unknown>;
      if (seen.has(obj)) throw new Error('Circular reference detected');
      seen.add(obj);

      if (Array.isArray(obj)) {
        for (let i = 0; i < obj.length; i++) {
          const v = obj[i];
          const name = buildName([...path, i]);
          if (v !== null && typeof v === 'object') {
            walk(v, [...path, i]);
          } else {
            push(name, v);
          }
        }
      } else {
        for (const key of Object.keys(obj)) {
          const v = obj[key];
          const name = buildName([...path, key]);
          if (v !== null && typeof v === 'object') {
            walk(v, [...path, key]);
          } else {
            push(name, v);
          }
        }
      }

      seen.delete(obj);
    } else {
      push(buildName(path), value);
    }
  };

  if (options?.prefix) {
    walk(input, [options.prefix]);
  } else if (typeof input === 'object' && input !== null && !Array.isArray(input)) {
    for (const key of Object.keys(input as Record<string, unknown>)) {
      walk((input as Record<string, unknown>)[key], [key]);
    }
  } else {
    // Primitive top-level: treat as single param "value"
    walk(input, ['value']);
  }

  return parts.join('&');
}
